// One team's season, read from the schedule (B-386): its game each week, how
// each went, and the sums a summary says. Any team's, not only mine: the
// schedule shows the season of whichever team is picked.

import type { MatchupSummary, SchedulePeriod, ScheduleView, TeamRef } from '@fsp/domain';

type Side = MatchupSummary['home'];

/** How a game went, or where it is: the winner as the result says (N1), never the scores. */
export type Outcome = 'win' | 'loss' | 'tie' | 'live' | 'pre';

export interface Week {
  p: SchedulePeriod;
  /** The team's game this week, or null. */
  game: MatchupSummary | null;
  us: Side | null;
  them: Side | null;
  /** At home (*vs*) rather than away (*at*). */
  home: boolean;
  /** A regular week the league plays and this team sits out (an odd team count). */
  bye: boolean;
  outcome: Outcome | null;
  /** The week's second game, in a league that plays two opponents a week (Q6). */
  second: boolean;
  /**
   * One of a week's two games the team sits out (Q6): an odd-sized league
   * rests two teams a game each week. Its row has no game, and `bye` is true.
   */
  rest: boolean;
}

/** The team's game in a week. */
export function gameOf(p: SchedulePeriod, teamId: string): MatchupSummary | null {
  return p.matchups.find((m) => m.home.team.id === teamId || m.away.team.id === teamId) ?? null;
}

export function outcomeOf(m: MatchupSummary, teamId: string): Outcome {
  if (m.status !== 'final') return m.status;
  if (m.winnerTeamId === teamId) return 'win';
  if (m.winnerTeamId === null) return 'tie';
  return 'loss';
}

export function weekOf(p: SchedulePeriod, teamId: string): Week {
  return weekWith(p, gameOf(p, teamId), teamId);
}

function weekWith(p: SchedulePeriod, game: MatchupSummary | null, teamId: string): Week {
  const home = game !== null && game.home.team.id === teamId;
  return {
    p,
    game,
    us: game === null ? null : home ? game.home : game.away,
    them: game === null ? null : home ? game.away : game.home,
    home,
    bye: game === null && p.kind === 'regular' && p.matchups.length > 0,
    outcome: game === null ? null : outcomeOf(game, teamId),
    second: game?.secondGame ?? false,
    rest: false,
  };
}

/**
 * The team's week as the season lists it: one row, or in a week of two
 * games (Q6, B-614) a row a game, its first first, and a game it sits out
 * (an odd-sized league) a row of its own saying so.
 */
export function weeksOf(p: SchedulePeriod, teamId: string): Week[] {
  const games = p.matchups
    .filter((m) => m.home.team.id === teamId || m.away.team.id === teamId)
    .sort((a, b) => Number(a.secondGame) - Number(b.secondGame));
  if (!p.matchups.some((m) => m.secondGame) || games.length === 0) return [weekOf(p, teamId)];
  if (games.length > 1) return games.map((g) => weekWith(p, g, teamId));
  const played = weekWith(p, games[0]!, teamId);
  const rest: Week = {
    ...weekWith(p, null, teamId),
    bye: true,
    rest: true,
    second: !played.second,
  };
  return played.second ? [rest, played] : [played, rest];
}

/** A row's key: its week, and which of the week's games. */
export function weekKey(w: Week): string {
  return `${w.p.period.index}:${w.second ? 2 : 1}`;
}

/**
 * Every team the season names: mine first, then by name. In a league played
 * against the field the weeks have no games, only my week against the
 * league (`myField`), so mine is the one team it names.
 */
export function teamsIn(view: ScheduleView): TeamRef[] {
  const seen = new Map<string, TeamRef>();
  for (const p of view.periods) {
    for (const m of p.matchups) {
      seen.set(m.home.team.id, m.home.team);
      seen.set(m.away.team.id, m.away.team);
    }
    if (p.myField !== null) seen.set(p.myField.team.id, p.myField.team);
  }
  return [...seen.values()].sort(
    (a, b) => Number(b.isMine) - Number(a.isMine) || a.name.localeCompare(b.name),
  );
}

/** The run of the same result the season ends on so far: "Lost 2 in a row". */
export function streakOf(
  weeks: readonly Week[],
): { outcome: 'win' | 'loss' | 'tie'; count: number } | null {
  const done = weeks.filter(
    (w): w is Week & { outcome: 'win' | 'loss' | 'tie' } =>
      w.outcome === 'win' || w.outcome === 'loss' || w.outcome === 'tie',
  );
  const last = done.at(-1);
  if (last === undefined) return null;
  let count = 0;
  for (let i = done.length - 1; i >= 0 && done[i]!.outcome === last.outcome; i -= 1) count += 1;
  return { outcome: last.outcome, count };
}

/**
 * Points scored and allowed over the weeks finished, and how many weeks: a
 * week of two games (Q6) its one score once and the mean of its opponents',
 * as the standings count them (B-614).
 */
export function pointsOf(weeks: readonly Week[]): {
  scored: number;
  allowed: number;
  games: number;
} {
  const byWeek = new Map<number, { scored: number; against: number[] }>();
  for (const w of weeks) {
    if (w.game?.status !== 'final' || w.us === null || w.them === null) continue;
    const week = byWeek.get(w.p.period.index) ?? { scored: w.us.score, against: [] };
    week.against.push(w.them.score);
    byWeek.set(w.p.period.index, week);
  }
  let scored = 0;
  let allowed = 0;
  for (const w of byWeek.values()) {
    scored += w.scored;
    allowed += w.against.reduce((sum, x) => sum + x, 0) / w.against.length;
  }
  return { scored, allowed, games: byWeek.size };
}

/** Each team's average score a week over its finished games, for a future opponent's line. */
export function averagesOf(view: ScheduleView): Map<string, number> {
  const sums = new Map<string, { points: number; games: number }>();
  for (const p of view.periods)
    for (const m of p.matchups) {
      if (m.status !== 'final') continue;
      for (const s of [m.home, m.away]) {
        const t = sums.get(s.team.id) ?? { points: 0, games: 0 };
        sums.set(s.team.id, { points: t.points + s.score, games: t.games + 1 });
      }
    }
  return new Map([...sums].map(([id, t]) => [id, t.points / t.games]));
}

/**
 * The week on screen at the top: the one under way, or else the next to
 * come with games in it; null once the season has none left.
 */
export function weekNow(view: ScheduleView, now: number): SchedulePeriod | null {
  const current = view.periods.find((p) => p.period.isCurrent && p.matchups.length > 0);
  if (current) return current;
  return (
    view.periods.find((p) => Date.parse(p.period.start) > now && p.matchups.length > 0) ?? null
  );
}

/** A space a line never breaks at. */
const NO_BREAK = String.fromCharCode(160);

/** "Oct 9–13", or "Sep 30 – Oct 5" across a month, in the reader's zone. */
export function weekDates(start: string, end: string, zone: string | null): string {
  const tz = zone ?? undefined;
  const from = new Date(start);
  // The end is the next week's start, early on its first morning (a week
  // turns over at dawn): the day twelve hours before it is this week's last.
  const to = new Date(Date.parse(end) - 12 * 3_600_000);
  const month = (d: Date) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: tz }).format(d);
  const day = (d: Date) =>
    new Intl.DateTimeFormat('en-US', { day: 'numeric', timeZone: tz }).format(d);
  // A month and its day never part (a no-break space): a narrow column
  // wraps a range at its dash, "Sep 29 –" over "Oct 5".
  const on = (d: Date) => `${month(d)}${NO_BREAK}${day(d)}`;
  return month(from) === month(to) ? `${on(from)}–${day(to)}` : `${on(from)} – ${on(to)}`;
}

/** "2–1", or "2–1–1" with ties. */
export function recordText(r: { wins: number; losses: number; ties: number }): string {
  return `${r.wins}–${r.losses}${r.ties ? `–${r.ties}` : ''}`;
}
